import * as ko from 'knockout'
import { BetInformers } from '@core/Areas/Racing/Components/BetInformers/BetInformers'
import { RacePanels } from '@core/Areas/Racing/Components/Panels'
import { StartersColumnHeader } from '@core/Areas/Racing/Components/RunnersColumnHeader'
import { RaceStatus } from '@core/Areas/Racing/Types'
import { Disposable } from '../../../../AppUtils/Framework/Disposable/Disposable'
import { publish } from '../../../../AppUtils/Framework/Messaging/EventAggregator'
import BetTypeInformation from '../../../Model/Betting/BetTypeInformation'
import type { BettingInformation } from '../../../Model/BettingInformation'
import type { IObservablePage } from '../../../Model/Observables/IObservablePage'
import type { IObservableStarter } from '../../../Model/Observables/IObservableStarter'
import type ObservableRaceKey from '../../../Model/Observables/ObservableRaceKey'
import type ObservableRacePage from '../../../Model/Observables/ObservableRacePage'
import type { IUseGiddyUp } from '../../Pages/Starters/IUseGiddyUp'
import { state$ as bettingV2CommandDriver$ } from '../driver'

export type Params = {
  raceInfo: IObservablePage
  betType: ko.Observable<BetTypeInformation>
  starters: ko.ObservableArray<IObservableStarter>
  raceKey: ObservableRaceKey
  useGiddyUp: IUseGiddyUp
  bettingContext: BettingInformation
}

type StartersColumnHeaderParams = Omit<
  React.ComponentProps<typeof StartersColumnHeader>,
  'children' | 'bettingContext'
>

export class InfoToolbarViewModel extends Disposable {
  public showBestPrice!: ko.PureComputed<boolean>
  public isDoubleSelected!: ko.Observable<boolean>
  public isQuaddieSelected!: ko.Observable<boolean>

  public raceInfo: IObservablePage
  public betType: ko.Observable<BetTypeInformation>
  public starters: ko.ObservableArray<IObservableStarter>
  public raceKey: ObservableRaceKey
  private subFormButton!: Rx.Disposable

  public BetInformers = BetInformers
  public StarterPageHeader = StartersColumnHeader
  public RacePanels = RacePanels
  public useGiddyUp: IUseGiddyUp
  public isGiddyUpDisabled!: boolean
  public toggleGiddyUp: () => void

  public bettingContext: BettingInformation
  public starterPageHeaderProps = ko.pureComputed(this.createStarterPageHeaderProps, this)

  constructor(params: Params) {
    super()
    this.bettingContext = params.bettingContext
    this.raceInfo = params.raceInfo
    this.betType = params.betType
    this.starters = params.starters
    this.raceKey = params.raceKey

    const raceNumber = this.raceKey.raceNumber()

    this.useGiddyUp = params.useGiddyUp
    this.isGiddyUpDisabled = !this.useGiddyUp.showGiddyUpInInformationPanel(raceNumber)
    this.toggleGiddyUp = this.useGiddyUp.openGiddyUp

    this.subFormButton = bettingV2CommandDriver$
      .map(record => ({ selected: record.showAllForm }))
      .distinctUntilChanged()
      .subscribe(result => {
        this.PublishFormMessages(result.selected)
      })

    this.registerDisposals(() => {
      this.subFormButton.dispose()
    })
  }

  private PublishFormMessages(selected: boolean) {
    publish('all-form-selected-command', {
      raceNumber: this.raceKey.raceNumber(),
      selected: selected,
    })
  }

  private createStarterPageHeaderProps(): StartersColumnHeaderParams {
    const selectedBetType = this.bettingContext.selectedBetType()
    const raceNumber = this.raceKey.raceNumber()
    const currentSortForRace = this.bettingContext.sortOption
    const raceInformation = this.raceInfo as unknown as ObservableRacePage
    const raceStatus = this.raceInfo.meetingInformation.selectedRace.raceStatus() as RaceStatus
    return {
      currentSortForRace: {
        property: currentSortForRace?.property || 'Number',
        direction: currentSortForRace?.direction || 'Ascending',
      },
      startersForRace: raceInformation
        .getStartersForRace(raceNumber)()
        .map(x => ({ hasFixedOdds: x.hasFixedOdds() })),
      selectedBetType: {
        betType: selectedBetType.betType(),
        isSameRaceMulti: selectedBetType.isSameRaceMulti(),
        isWinPlace: selectedBetType.isWinPlace(),
        sortOptions: selectedBetType.sortOptions(),
      },
      raceNumber,
      raceStatus,
    }
  }
}
